import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useUser, useCompany } from '../contexts';
import { isAdminUserId, isBlockedUserId } from '../utils/userFlags';
import { isPublicRoute } from '../utils/publicRoutes';
import { setPendingRedirect } from '../utils/pendingRedirect';
import {
    ONBOARDING_PATHS,
    ONBOARDING_ROOT,
    getResumeOnboardingPath,
    hasSeenOnboardingAnimation
} from '../pages/onboardingRoutes';

// Routes that an authenticated user with no companyId can legitimately be on.
// /onboarding/* is handled separately by the startsWith check below.
const ONBOARDING_BYPASS_PATHS = new Set([
    '/sign-up',
    '/login',
    '/accept-invite',
    '/claim',
    '/privacy-policy',
    '/terms-conditions',
    // The MCP connector consent screen. A logged-out visitor is sent from here
    // to /sign-up (ConnectAI parks the OAuth params and redirects), so it must
    // render for logged-out users rather than being pre-empted to /login. It
    // also renders for logged-in users with NO workspace yet — they Authorize
    // here and then run `create_workspace` from inside the AI client — so it
    // must not be bounced to /onboarding/business either.
    '/connect-ai',
]);

function RequireOnboarding({ children }) {
    const { user, loading: userLoading } = useUser();
    const {
        companyId,
        companyData,
        unipileAccounts,
        unipileAccountsLoading,
        loading: companyLoading
    } = useCompany();
    const location = useLocation();

    // Don't redirect mid-load.
    if (userLoading || companyLoading) return children;

    // Logged-OUT users hitting an auth-required app route get sent to /login
    // with their intended destination preserved durably (localStorage, so it
    // survives the Login<->SignUp hop and full reloads). Public routes — share
    // links, demos, pricing, policy pages, OAuth callbacks, claim/accept-invite
    // — render as-is and are never gated. setPendingRedirect self-validates, so
    // /login, /sign-up, /onboarding/*, /claim, /accept-invite can never be
    // stored even if a public route slipped through.
    if (!user?.userId) {
        if (isPublicRoute(location.pathname)) return children;
        if (ONBOARDING_BYPASS_PATHS.has(location.pathname)) return children;
        setPendingRedirect(location.pathname + location.search);
        return <Navigate to="/login" replace />;
    }

    // Platform admins can roam any URL — matches the exemptions in
    // OnboardingFlow.js so internal testing of dashboard pages still works.
    if (isAdminUserId(user.userId)) return children;

    // Blocked users are funnelled to '/' by Layout.js. If we redirected them
    // into onboarding instead, Layout would bounce them right back and we'd
    // ping-pong forever. Defer to Layout's existing handling.
    if (isBlockedUserId(user.userId)) return children;

    // Already inside the onboarding flow — its own guard handles routing.
    if (location.pathname.startsWith(ONBOARDING_ROOT)) return children;

    // Pages a user without a workspace can legitimately reach (auth screens,
    // invite acceptance, public policy pages).
    if (ONBOARDING_BYPASS_PATHS.has(location.pathname)) return children;

    // No companyId is independent of the unipile snapshot — and we MUST decide
    // first, because CompanyContext leaves unipileAccounts as null forever when
    // there's no companyId, so waiting on unipileAccountsLoading would block
    // the redirect indefinitely.
    if (!companyId) return <Navigate to={ONBOARDING_PATHS.business} replace />;

    // For the new-flow email-step check we need the unipile snapshot settled —
    // unipileAccounts starts null and becomes [] after the first snapshot.
    // Without the wait we'd briefly think "no inbox" and bounce a fully
    // set-up user to /onboarding/email.
    if (unipileAccountsLoading) return children;

    const resumePath = getResumeOnboardingPath({ user, companyId, unipileAccounts, companyData, animationSeen: hasSeenOnboardingAnimation() });
    if (resumePath) return <Navigate to={resumePath} replace />;

    return children;
}

export default RequireOnboarding;
